/**
 * Currency and Number Formatting Utilities
 * Consistent formatting for financial data throughout the application
 */

/**
 * Format currency values with proper localization
 */
export const formatCurrency = (amount: number | string, currency: string = 'USD'): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

/**
 * Format currency in compact form (e.g., $1.2K, $1.5M)
 */
export const formatCurrencyCompact = (amount: number | string, currency: string = 'USD'): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '$0';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numAmount);
};

/**
 * Format percentage values
 */
export const formatPercentage = (value: number | string, decimals: number = 1): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '0.0%';
  }

  return `${numValue.toFixed(decimals)}%`;
};

/**
 * Format numbers with thousands separators
 */
export const formatNumber = (value: number | string): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '0';
  }

  return new Intl.NumberFormat('en-US').format(numValue);
};

/**
 * Format numbers in compact form (e.g., 1.2K, 1.5M)
 */
export const formatNumberCompact = (value: number | string): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '0';
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numValue);
};

/**
 * Growth indicator component data
 */
export interface GrowthData {
  value: number;
  isPositive: boolean;
  color: string;
  icon: string;
  formattedValue: string;
}

/**
 * Get growth indicator data for UI components
 */
export const getGrowthIndicator = (value: number | string): GrowthData => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  const isPositive = numValue >= 0;
  
  return {
    value: numValue,
    isPositive,
    color: isPositive ? '#4caf50' : '#f44336',
    icon: isPositive ? '↗' : '↘',
    formattedValue: formatPercentage(Math.abs(numValue))
  };
};

/**
 * Calculate percentage change between two values
 */
export const calculatePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  
  return ((current - previous) / previous) * 100;
};

/**
 * Format date for revenue analytics
 */
export const formatAnalyticsDate = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(dateObj);
};

/**
 * Format date for charts (shorter format)
 */
export const formatChartDate = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric'
  }).format(dateObj);
};

/**
 * Get time range display text
 */
export const getTimeRangeText = (timeRange: string): string => {
  switch (timeRange) {
    case '7d': return 'Last 7 days';
    case '30d': return 'Last 30 days';
    case '90d': return 'Last 90 days';
    case '1y': return 'Last year';
    default: return 'Last 30 days';
  }
};

/**
 * Revenue status colors
 */
export const REVENUE_COLORS = {
  primary: '#1976d2',
  secondary: '#9c27b0',
  success: '#2e7d32',
  error: '#d32f2f',
  warning: '#ed6c02',
  info: '#0288d1',
  subscription: '#1976d2',
  oneTime: '#9c27b0',
  growth: '#2e7d32',
  decline: '#d32f2f',
} as const;

/**
 * Transaction status colors
 */
export const TRANSACTION_STATUS_COLORS = {
  completed: '#2e7d32',
  pending: '#ed6c02',
  failed: '#d32f2f',
  refunded: '#757575',
} as const;

/**
 * Get status color for transactions
 */
export const getTransactionStatusColor = (status: string): string => {
  return TRANSACTION_STATUS_COLORS[status as keyof typeof TRANSACTION_STATUS_COLORS] || '#757575';
};

/**
 * Format revenue metrics for display
 */
export const formatRevenueMetric = (
  value: number | string,
  type: 'currency' | 'percentage' | 'number' | 'compact-currency' | 'compact-number' = 'currency'
): string => {
  switch (type) {
    case 'currency':
      return formatCurrency(value);
    case 'compact-currency':
      return formatCurrencyCompact(value);
    case 'percentage':
      return formatPercentage(value);
    case 'number':
      return formatNumber(value);
    case 'compact-number':
      return formatNumberCompact(value);
    default:
      return formatCurrency(value);
  }
};